import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';

import { AuthGuard } from '@app/core/auth/auth.guard';
import { Error } from '@app/core/errors/errors.model';

import { AppComponent } from './app.component';
import { BENEFICIARIES_DEFAULT_ROUTE } from './beneficiaries/constants/routes';
import { INVOICES_BASE_ROUTE } from './boletos/boletos.routes';
import { ErrorsComponent } from './core/errors/errors-component/errors.component';
import { DOCUMENTS_ROUTE } from './documents/documents.routes';
import { ExternalJuniorHiringRoutesEnum } from './hiring/external/junior/external-junior-hiring.routes';
import { JuniorHiringRoutesEnum } from './hiring/junior-hiring/junior-hiring.routes';
import { MOBILE_SEARCH_ROUTE } from './mobile-search/mobile-search.routes';
import { NEW_DIVERSIFICATION_DEFAULT_ROUTE } from './new-diversification/constants/routes';
import { NO_PLANS_ROUTE } from './no-plans/no-plans.constants';
import { OPEN_INSURANCE_ROUTE } from './open-insurance/shared/constants/routes';
import { PLAN_BASE_ROUTE } from './plan/plan.model';
import { PlanTradUnsignedGuard } from './plan-blocked.guard';
import { PLAN_MANAGER_BASE_ROUTE } from './plan-manager/plan-manager.routes';
import { PlatformStore } from './platform-store.resolver';
import { UnsignedContractGuard } from './unsigned-contract.guard';

const routes: Routes = [
  {
    path: '',
    component: AppComponent,
    pathMatch: 'full',
    resolve: { resolve: PlatformStore }
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule)
  },
  {
    path: 'profile',
    loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'privacy',
    loadChildren: () => import('./lgpd/lgpd.module').then((m) => m.LgpdModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'validation',
    loadChildren: () =>
      import('./validation/validation.module').then((m) => m.ValidationModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'renegotiation',
    loadChildren: () =>
      import('./renegotiation/renegotiation.module').then(
        (m) => m.RenegotiationModule
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'home',
    loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: PLAN_MANAGER_BASE_ROUTE,
    loadChildren: () =>
      import('./plan-manager/plan-manager.module').then(
        (m) => m.PlanManagerModule
      ),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: BENEFICIARIES_DEFAULT_ROUTE,
    loadChildren: () =>
      import('./beneficiaries/beneficiaries.module').then(
        (m) => m.BeneficiariesModule
      ),
    canActivate: [AuthGuard, UnsignedContractGuard, PlanTradUnsignedGuard]
  },
  {
    path: JuniorHiringRoutesEnum.BASE,
    loadChildren: () =>
      import('./hiring/junior-hiring/junior-hiring.module').then(
        (m) => m.JuniorHiringModule
      ),
    canActivate: [AuthGuard, UnsignedContractGuard]
  },
  {
    path: NEW_DIVERSIFICATION_DEFAULT_ROUTE,
    loadChildren: () =>
      import('./new-diversification/new-diversification.module').then(
        (m) => m.NewDiversificationModule
      ),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: PLAN_BASE_ROUTE,
    loadChildren: () => import('./plan/plan.module').then((m) => m.PlanModule),
    canActivate: [AuthGuard, UnsignedContractGuard]
  },
  {
    path: 'withdraw',
    loadChildren: () =>
      import('./withdraw/withdraw.module').then((m) => m.WithdrawModule),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: 'checkup',
    loadChildren: () =>
      import('./checkup/checkup.module').then((m) => m.CheckupModule),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: 'decumulation',
    loadChildren: () =>
      import('./decumulation/decumulation.module').then(
        (m) => m.DecumulationModule
      ),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: 'upselling',
    loadChildren: () =>
      import('./upselling/upselling.module').then((m) => m.UpsellingModule),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: INVOICES_BASE_ROUTE,
    loadChildren: () =>
      import('./boletos/boletos.module').then((m) => m.BoletosModule),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: 'support',
    loadChildren: () =>
      import('./support/support.module').then((m) => m.SupportModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'certificate',
    loadChildren: () =>
      import('./certificate/certificate.module').then(
        (m) => m.CertificateModule
      ),
    canActivate: [AuthGuard]
  },
  {
    path: DOCUMENTS_ROUTE,
    loadChildren: () =>
      import('./documents/documents.module').then((m) => m.DocumentsModule),
    canActivate: [AuthGuard]
  },
  {
    path: OPEN_INSURANCE_ROUTE,
    loadChildren: () =>
      import('./open-insurance/open-insurance.module').then(
        (m) => m.OpenInsuranceModule
      ),
    canActivate: [AuthGuard]
  },
  {
    path: MOBILE_SEARCH_ROUTE,
    loadChildren: () =>
      import('./mobile-search/mobile-search.module').then(
        (m) => m.MobileSearchModule
      ),
    canActivate: [AuthGuard, PlanTradUnsignedGuard]
  },
  {
    path: ExternalJuniorHiringRoutesEnum.BASE,
    loadChildren: () =>
      import('./hiring/external/junior/external-junior-hiring.module').then(
        (m) => m.ExternalJuniorPlanHiringModule
      )
  },
  {
    path: NO_PLANS_ROUTE,
    loadChildren: () =>
      import('./no-plans/no-plans.module').then((m) => m.NoPlansModule),
    canActivate: [AuthGuard]
  },
  { path: 'error', component: ErrorsComponent },
  {
    path: '**',
    component: ErrorsComponent,
    data: { error: 404 } as Error
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      scrollPositionRestoration: 'enabled'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
